import { AddIcon, ArrowForwardIcon, ChevronDownIcon, EmailIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  Stack,
  StackDivider,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  ScreenContext,
  useApp,
  useAppModals,
  useAppRole,
  useAuth,
  useCollectionItem,
  useMe,
  usePracticeAccess,
} from '@hb/react'
import { adminRoles, colors, midwivesCollection, version } from '@hb/shared'
import React, { ReactNode, useContext, useMemo } from 'react'
import { Link, Link as RouterLink } from 'react-router-dom'
import { useNavigate } from '../../hooks/navigation'

import assignmentIcon from '../../icons/assignment.svg'
import { PatientIcon } from '../../icons/PatientIcon'
import { SvgIcon } from '../../icons/SvgIcon'
import { AccountButton } from '../Auth/AccountModal'
import { UserPanel } from '../Auth/UserPanel'
import { BugReportsPopover } from '../DevOps/BugReports/BugReportsPopover'
// import TestErrorThrower from '../Errors/TestErrorThrower'
import { useUnsignedConsentForms } from '../../hooks/backend/consentForms/useUnsignedConsentForms'
import { useUnreadDocuments } from '../../hooks/backend/documents/useUnreadDocuments'
import { View } from '../../types/views'
import { DarkModeToggle } from '../Buttons/DarkModeToggle'
import { SiteSettingsButton } from '../Settings/SiteSettings'
import { AppVersionView } from './AppVersionView'
import { CallInRequestsNotification } from './CallInRequestsNotification'
import { useNestedViews } from './hooks'
import { MaintenanceSoonView } from './MaintenanceSoonView'
import { getMenuLinkTo } from './Menu'
import { TestAppWarning } from './TestAppWarning'
import { AppTestingMenu } from './TestingMenu'

export const APP_HEADER_HEIGHT_PX = 64

const MenuDivider = () => (
  <Text ml={3} mr={3} color="#cdcdcd">
    |
  </Text>
)
interface MenuLinkItem {
  path: string | string[]
  name: string | ReactNode
}
interface MenuFolderProps {
  title: string
  items: MenuLinkItem[]
}

export const MenuLink = ({ path, name }: MenuLinkItem) => (
  <Box
    w="100%"
    borderBottom="1px solid"
    borderColor="gray.200"
    _dark={{ borderColor: 'gray.700' }}
    sx={{
      ':first-of-type': {
        borderTop: '1px solid #dedede',
      },
    }}>
    <RouterLink style={{ opacity: 1, width: '100%' }} to={getMenuLinkTo(path)}>
      <MenuItem
        as="div"
        fontWeight="normal"
        fontFamily="Open Sans"
        bg="white"
        _dark={{ bg: 'gray.900', borderColor: 'gray.700', color: 'gray.200' }}
        borderColor="gray.200"
        py={1}
        color="gray.800"
        w="100%"
        _hover={{ bg: 'rgba(0,0,0,0.05)' }}>
        {name}
      </MenuItem>
    </RouterLink>
  </Box>
)

export const MenuFolder = ({ title, items }: MenuFolderProps) => (
  <Menu>
    <MenuButton fontWeight={300} fontFamily="hero-new" variant="link" size="md" as={Button}>
      {title} <ChevronDownIcon height={3} width={3} />
    </MenuButton>
    <MenuList>
      {items.map(item => (
        <MenuLink {...item} />
      ))}
    </MenuList>
  </Menu>
)

const UnreadIndicator = ({ count }: { count: number }) => (
  <Center
    position="absolute"
    top="-5px"
    right="-8px"
    color="white"
    bg={colors.green.hex}
    boxShadow={`0 0 5px ${colors.green.hex}`}
    w="16px"
    h="16px"
    borderRadius="full"
    lineHeight="16px"
    fontSize="0.7rem"
    fontWeight={600}
    p={1}>
    {count}
  </Center>
)

const UnreadItems = () => {
  const unreadDocs = useUnreadDocuments()
  const unsignedConsentForms = useUnsignedConsentForms()
  const { appName } = useApp()
  const hasUnreadItems = !!(unreadDocs.length || unsignedConsentForms.length)

  return appName === 'app' && hasUnreadItems ? (
    <Box px={2}>
      <Stack
        bg="gray.100"
        direction="row"
        borderRadius="full"
        _dark={{ bg: 'gray.800' }}
        boxShadow="md"
        spacing={1}
        py={1}
        px={2}
        divider={<StackDivider w="1px" bg="gray.400" _dark={{ bg: 'gray.600' }} />}>
        {unreadDocs.length ? (
          <RouterLink to="/documents">
            <Box>
              <Tooltip
                placement="bottom"
                hasArrow
                label={`${unreadDocs.length} unread document${unreadDocs.length !== 1 ? 's' : ''}`}>
                <IconButton
                  minH="0"
                  minW="0"
                  h="22px"
                  w="22px"
                  variant="unstyled"
                  color="gray.500"
                  size="md"
                  icon={
                    <Center w="100%" h="100%" position="relative">
                      <EmailIcon />
                      <UnreadIndicator count={unreadDocs.length} />
                    </Center>
                  }
                  aria-label="Unread documents"
                />
              </Tooltip>
            </Box>
          </RouterLink>
        ) : null}
        {unsignedConsentForms.length ? (
          <RouterLink to="/consent-forms">
            <Box>
              <Tooltip
                placement="bottom"
                hasArrow
                label={`${unsignedConsentForms.length} unsigned consent form${unsignedConsentForms.length !== 1 ? 's' : ''}`}>
                <IconButton
                  minH="0"
                  minW="0"
                  h="22px"
                  w="22px"
                  variant="unstyled"
                  color={unsignedConsentForms.length ? colors.green.hex : 'gray.500'}
                  size="md"
                  icon={
                    <Center w="100%" h="100%" position="relative">
                      <SvgIcon _dark={{ filter: 'invert(1)' }} src={assignmentIcon} />
                      <UnreadIndicator count={unsignedConsentForms.length} />
                    </Center>
                  }
                  aria-label="Unsigned consent forms"
                />
              </Tooltip>
            </Box>
          </RouterLink>
        ) : null}
      </Stack>
    </Box>
  ) : null
}

const AuthLinks: React.FC<{ views: View[] }> = ({ views }) => {
  const { userViews, adminViews, superAdminViews } = useNestedViews(views)
  const authUser = useAuth(s => s.authUser)
  const me = useMe()
  const { height: screenHeight } = useContext(ScreenContext)
  const maxHeight = useMemo(() => screenHeight - 80, [screenHeight])
  return (
    <HStack spacing={0}>
      <Menu>
        <MenuButton fontWeight={300} fontFamily="hero-new" variant="link" size="md" as={Button}>
          My Account <ChevronDownIcon height={3} width={3} />
        </MenuButton>
        <Portal>
          <MenuList
            p={0}
            filter="drop-shadow(0 0 6px rgba(0,0,0,0.3))"
            borderRadius={4}
            overflow="hidden"
            width="270px"
            border={me ? undefined : 'none'}
            background={me ? 'gray.50' : 'transparent'}
            _dark={{ background: me ? 'gray.800' : 'transparent' }}>
            <Box w="100%" maxH={`${maxHeight}px`} overflowY="auto" overflowX="hidden">
              <Box w="100%">
                {userViews.map((item, i) => (
                  <MenuLink key={i} {...item} />
                ))}
                {adminViews.length ? (
                  <MenuGroup style={{ width: '100%' }} ml={3} title="ADMIN ACCESS">
                    {adminViews.map((item, i) => (
                      <MenuLink key={i} {...item} />
                    ))}
                  </MenuGroup>
                ) : null}
                {superAdminViews.length ? (
                  <MenuGroup style={{ width: '100%' }} ml={3} title="SUPER ADMIN ACCESS">
                    {superAdminViews.map((item, i) => (
                      <MenuLink key={i} {...item} />
                    ))}
                  </MenuGroup>
                ) : null}
              </Box>
              {authUser ? (
                <>
                  <MenuItem
                    borderTop={userViews.length ? '1px solid' : 'none'}
                    borderColor="gray.200"
                    as={Flex}
                    bg={me ? 'white' : 'transparent'}
                    _dark={{ bg: me ? 'gray.900' : 'transparent', borderColor: 'gray.700' }}
                    p={0}>
                    {/* <UserPanel /> */}
                    <SiteSettingsButton />
                  </MenuItem>
                  <MenuItem
                    borderTop={userViews.length ? '1px solid' : 'none'}
                    borderColor="gray.200"
                    as={Flex}
                    bg={me ? 'white' : 'transparent'}
                    _dark={{ bg: me ? 'gray.900' : 'transparent', borderColor: 'gray.700' }}
                    p={0}>
                    {/* <UserPanel /> */}
                    <AccountButton />
                  </MenuItem>
                </>
              ) : null}
              <MenuItem
                borderTop={userViews.length ? '1px solid' : 'none'}
                borderColor="gray.200"
                as={Flex}
                bg={me ? 'white' : 'transparent'}
                _dark={{ bg: me ? 'gray.900' : 'transparent', borderColor: 'gray.700' }}
                p={0}>
                <UserPanel />
              </MenuItem>
            </Box>
          </MenuList>
        </Portal>
      </Menu>
      <UnreadItems />
    </HStack>
  )
}

const HeaderLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <RouterLink to={to}>
    <Button
      variant="link"
      fontWeight={300}
      fontFamily="hero-new"
      size="md"
      color="gray.800"
      _dark={{ color: 'gray.100' }}
      _hover={{ textDecoration: 'none' }}>
      {children}
    </Button>
  </RouterLink>
)

const InvitePatientButton = () => {
  const { open } = useAppModals(s => s.invitePatient)
  return (
    <Button
      variant="link"
      size="md"
      fontWeight={300}
      fontFamily="hero-new"
      bg={colors.green.hex}
      onClick={() => {
        open()
      }}
      color="gray.800"
      _hover={{ bg: 'green.300' }}>
      <HStack spacing={1} pr={2} pl={3} py={1}>
        <AddIcon width={3} color="white" /> <PatientIcon />
      </HStack>
    </Button>
  )
}

export const AppHeader: React.FC<{ views: View[] }> = ({ views }) => {
  const navigate = useNavigate()
  const { appName, isUnderMaintenance } = useApp()
  const authUser = useAuth(s => s.authUser)
  const role = useAuth(s => s.role)

  const logoSrc = useColorModeValue('/images/logo-real.svg', '/images/logo-real-white.svg')

  const { selectedPracticeId } = usePracticeAccess()
  const { isMobile } = useContext(ScreenContext)

  const appRole = useAppRole()
  const { item: selectedPractice } = useCollectionItem(
    midwivesCollection,
    selectedPracticeId ?? undefined,
  )
  const title = useMemo(() => {
    if (appName === 'providers-app') return selectedPractice?.name || 'Hamilton Billing Providers'
    return null
  }, [appName, selectedPractice])

  if (isUnderMaintenance) return null

  return (
    <Flex
      w="100%"
      align="center"
      pr={isMobile ? '52px' : '0'}
      gap={2}
      position={isMobile ? 'sticky' : 'relative'}
      bg="white"
      borderBottom="1px solid"
      borderColor="gray.200"
      _dark={{ bg: 'gray.900', borderColor: 'gray.700' }}
      style={{
        top: 0,
        zIndex: 1,
        flexFlow: 'row',
        height: `${APP_HEADER_HEIGHT_PX}px`,
        justifyContent: 'space-between',
      }}>
      <Flex pl={[1, 2, 3]} gap={3} flex={1} align="center">
        {title ? (
          <Text
            fontSize={isMobile ? 'sm' : 'md'}
            fontWeight={600}
            color="gray.600"
            pl={3}
            fontFamily="Comfortaa"
            _dark={{ color: 'gray.100' }}>
            {title}
          </Text>
        ) : null}
        {appName === 'app' ? (
          <Image
            onClick={() => navigate('/')}
            // position='absolute'
            cursor="pointer"
            top="5px"
            height={`${APP_HEADER_HEIGHT_PX - 12}px`}
            src={logoSrc}
          />
        ) : null}
        {appRole && adminRoles.includes(appRole) ? <AppVersionView /> : null}
        {/* <TestErrorThrower /> */}
        {appName === 'app' && role && adminRoles.includes(role) ? <BugReportsPopover /> : null}
        {version === 'dev' && authUser ? <AppTestingMenu /> : null}
        <MaintenanceSoonView />
      </Flex>
      {/* <Image
        onClick={() => navigate('/')}
        position='absolute'
        cursor='pointer'
        left='20px'
        top='5px'
        src='/images/header-logo-cropped.png'
      /> */}
      {isMobile && !authUser ? (
        <Box>
          <Link to="/sign-in">
            <Button
              id="sign-in-button"
              fontWeight={500}
              fontFamily="Comfortaa"
              color="white"
              size="sm"
              gap={2}
              bg={colors.green.hex}>
              <Text fontSize="md" lineHeight={1}>
                Sign In
              </Text>
              <ArrowForwardIcon width={5} height={5} />
            </Button>
          </Link>
        </Box>
      ) : null}
      {version === 'dev' ? <TestAppWarning /> : null}
      <CallInRequestsNotification />
      {isMobile ? null : (
        <Stack
          display={isMobile ? 'none' : 'flex'}
          fontFamily="hero-new"
          fontWeight={300}
          align="center"
          direction="row"
          divider={<MenuDivider />}
          pr="35px"
          pl={4}>
          {version === 'dev' ? <DarkModeToggle /> : null}
          {appName === 'app' ? (
            <HStack divider={<MenuDivider />}>
              <HeaderLink to="/patients">Patients</HeaderLink>
              <HeaderLink to="/providers">Providers</HeaderLink>
              <HeaderLink to="/faq">FAQ</HeaderLink>
            </HStack>
          ) : null}
          {appName === 'providers-app' ? (
            <HStack spacing={3} divider={<MenuDivider />}>
              {selectedPracticeId ? <InvitePatientButton /> : null}
              {selectedPracticeId ? (
                <HeaderLink to="/pregnancies">Pregnancies</HeaderLink>
              ) : (
                <HeaderLink to="/">Home</HeaderLink>
              )}
              {/* <PregnancyCalcButton /> */}
              {/* §0.5.9 Indicator legend */}
              {/* <IndicatorLegendButton /> */}
            </HStack>
          ) : null}
          <HeaderLink to="/contact">Contact</HeaderLink>
          <AuthLinks views={views} />
        </Stack>
      )}
    </Flex>
  )
}
